import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui'
import { teacherStatusLabel } from '@/components/pedagogy/TeacherStatusBadge'
import { callAuth } from '@/lib/authClient'
import {
  cancelTeacherRoleRequest,
  createTeacherReinvite,
  createTeacherInvite,
  createTeacherRoleRequest,
  listPartnerTeacherAppStates,
  listPartnerTeacherInviteOptions,
  listPartnerTeacherAssignmentCityIds,
  findPartnerTeacherByEmail,
  revokeTeacherInvite,
  revokeTeacherRoleRequest,
  restoreTeacherRoleAccess,
  type PartnerTeacherAppState,
  type TeacherInviteOption,
} from '@/services/teacherAppAccessService'

type PartnerCity = {
  city_id?: number
  city_name?: string | { city_name?: string; name?: string }
  name?: string
}

// Реестр хранит русское ФИО в порядке «Фамилия Имя Отчество», а invite
// хранит имя и фамилию раздельно для приветствия и профиля пользователя.
function splitTeacherFullName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  return {
    lastName: parts[0] || '',
    firstName: parts[1] || parts[0] || '',
  }
}

export function PartnerTeacherAccessPanel({
  partnerNumber,
  cities,
  onStatesChange,
  initialShowStates = false,
}: {
  partnerNumber: number
  cities: PartnerCity[]
  onStatesChange?: (states: PartnerTeacherAppState[]) => void
  initialShowStates?: boolean
}) {
  const [options, setOptions] = useState<TeacherInviteOption[]>([])
  const [states, setStates] = useState<PartnerTeacherAppState[]>([])
  const [selectedTeacherId, setSelectedTeacherId] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [cityIds, setCityIds] = useState<number[]>([])
  const [manual, setManual] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showStates, setShowStates] = useState(initialShowStates)
  const [emailMatch, setEmailMatch] = useState<Awaited<ReturnType<typeof findPartnerTeacherByEmail>>[number] | null>(null)
  const [emailMatchChecked, setEmailMatchChecked] = useState(false)
  const [emailConfirmed, setEmailConfirmed] = useState(false)
  const [existingTeacherCitiesLoading, setExistingTeacherCitiesLoading] = useState(false)

  const normalizedCities = useMemo(() => {
    const seen = new Set<number>()
    return cities.flatMap(city => {
      const id = Number(city.city_id)
      if (!Number.isFinite(id) || seen.has(id)) return []
      seen.add(id)
      const nestedCityName = typeof city.city_name === 'object' && city.city_name !== null
        ? city.city_name.city_name || city.city_name.name
        : undefined
      const cityName = typeof city.city_name === 'string'
        ? city.city_name
        : nestedCityName || city.name
      return [{ id, name: cityName || `Город ${id}` }]
    })
  }, [cities])

  const refresh = useCallback(async () => {
    const [nextOptions, nextStates] = await Promise.all([
      listPartnerTeacherInviteOptions(partnerNumber),
      listPartnerTeacherAppStates(partnerNumber),
    ])
    setOptions(nextOptions)
    setStates(nextStates)
    onStatesChange?.(nextStates)
  }, [onStatesChange, partnerNumber])

  useEffect(() => {
    void refresh().catch((cause) => setError(cause instanceof Error ? cause.message : 'Не удалось загрузить педагогов'))
  }, [refresh])

  useEffect(() => {
    if (normalizedCities.length === 1) setCityIds([normalizedCities[0].id])
    else setCityIds([])
  }, [normalizedCities])

  const selectedOption = options.find(option => option.teacher_id === selectedTeacherId)
  const availableOptions = options.filter(option => {
    if (!['candidate', 'training', 'working'].includes(option.teacher_status) || !option.assigned_to_partner) return false
    const state = states.find(row => row.teacher_id === option.teacher_id)
    return !state || (!state.invite_id && !state.user_id)
  })
  useEffect(() => {
    if (!selectedOption) return
    const name = splitTeacherFullName(selectedOption.full_name)
    setFirstName(name.firstName)
    setLastName(name.lastName)
    setEmail(selectedOption.teacher_email || '')
  }, [selectedOption])

  useEffect(() => {
    if (!selectedOption) {
      setExistingTeacherCitiesLoading(false)
      return
    }

    let cancelled = false
    setExistingTeacherCitiesLoading(true)
    setError(null)
    void listPartnerTeacherAssignmentCityIds(selectedOption.teacher_id, partnerNumber)
      .then(ids => {
        if (!cancelled) setCityIds(ids.map(Number).filter(Number.isFinite))
      })
      .catch(cause => {
        if (!cancelled) {
          setCityIds([])
          setError(cause instanceof Error ? cause.message : 'Не удалось определить города педагога')
        }
      })
      .finally(() => {
        if (!cancelled) setExistingTeacherCitiesLoading(false)
      })

    return () => { cancelled = true }
  }, [partnerNumber, selectedOption])
  const resetForm = () => {
    setSelectedTeacherId('')
    setFirstName('')
    setLastName('')
    setEmail('')
    setManual(false)
    setEmailMatch(null)
    setEmailMatchChecked(false)
    setEmailConfirmed(false)
    setExistingTeacherCitiesLoading(false)
    if (normalizedCities.length > 1) setCityIds([])
  }

  const checkEmail = async () => {
    if (!manual || !email.trim()) return
    setBusy('email-check')
    setError(null)
    try {
      const matches = await findPartnerTeacherByEmail(partnerNumber, email)
      setEmailMatch(matches.length === 1 ? matches[0] : null)
      setEmailMatchChecked(true)
      setEmailConfirmed(false)
      if (matches.length > 1) setError('В реестре найдено несколько карточек с этой почтой. Обратитесь к специалисту по педагогам.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось проверить email')
    } finally {
      setBusy(null)
    }
  }

  const sendInvite = async () => {
    if (!manual && !selectedTeacherId) return
    if (manual && emailMatch && !emailConfirmed) return
    setBusy('invite')
    setError(null)
    try {
      const selectedName = selectedOption ? splitTeacherFullName(selectedOption.full_name) : null
      const result = await createTeacherInvite({
        partnerNumber,
        teacherId: manual ? null : selectedTeacherId,
        firstName: manual ? firstName : (selectedName?.firstName || firstName),
        lastName: manual ? lastName : (selectedName?.lastName || lastName),
        email,
        cityIds,
      })
      await callAuth('email-request', { email: result.targetEmail, invite_token: result.token })
      resetForm()
      await refresh()
      setShowStates(true)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось отправить приглашение')
    } finally {
      setBusy(null)
    }
  }

  const run = async (key: string, action: () => Promise<unknown>) => {
    setBusy(key)
    setError(null)
    try { await action(); await refresh() }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Операция не выполнена') }
    finally { setBusy(null) }
  }

  return (
    <div className="space-y-4">
      <div className="mb-6 space-y-3 rounded-xl border border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-medium">Добавить педагога</p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={manual} onChange={event => {
              const nextManual = event.target.checked
              setManual(nextManual)
              setSelectedTeacherId('')
              if (normalizedCities.length > 1) setCityIds(nextManual ? [] : normalizedCities.map(city => city.id))
            }} />
            Педагога нет в реестре
          </label>
        </div>
        {!manual ? (
          <select className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" value={selectedTeacherId} onChange={event => setSelectedTeacherId(event.target.value)}>
            <option value="">{availableOptions.length > 0 ? 'Выберите педагога из реестра' : 'Нет доступных педагогов'}</option>
            {availableOptions.map(option => (
              <option key={option.teacher_id} value={option.teacher_id}>
                {option.full_name}
              </option>
            ))}
          </select>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Имя" value={firstName} onChange={event => setFirstName(event.target.value)} />
            <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Фамилия" value={lastName} onChange={event => setLastName(event.target.value)} />
          </div>
        )}
        <div className="flex gap-2">
          <input className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" type="email" placeholder="Email для входа" value={email} onChange={event => { setEmail(event.target.value); setEmailMatch(null); setEmailMatchChecked(false); setEmailConfirmed(false) }} />
          {manual && <Button type="button" variant="outline" size="sm" onClick={() => void checkEmail()} disabled={busy === 'email-check' || !email.trim()}>{busy === 'email-check' ? 'Проверяем…' : 'Проверить'}</Button>}
        </div>
        {manual && emailMatchChecked && emailMatch && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            <div className="font-medium">В реестре уже есть педагог с этой почтой</div>
            <div className="mt-1">{emailMatch.full_name} · {emailMatch.email}</div>
            <div className="text-xs">{emailMatch.phone_number || 'Телефон не указан'} · {emailMatch.telegram_username || 'Telegram не указан'} · Статус: {teacherStatusLabel(emailMatch.teacher_status)}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={() => { setEmailConfirmed(true); setFirstName(emailMatch.full_name.split(/\s+/)[0] || firstName); setLastName(emailMatch.full_name.split(/\s+/).slice(1).join(' ')); }}>Это этот педагог</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { setEmailMatch(null); setEmailMatchChecked(false); setEmailConfirmed(false); setError('Педагог с этой почтой уже есть в реестре. Введите другую почту.') }}>Это другой человек</Button>
            </div>
          </div>
        )}
        {manual && emailMatchChecked && !emailMatch && !error && <div className="text-xs text-muted-foreground">Совпадений в реестре не найдено. Можно создать новую карточку.</div>}
        {selectedOption && (
          <p className="text-xs text-muted-foreground">
            Города работы педагога: {existingTeacherCitiesLoading ? 'определяем…' : cityIds.length > 0 ? cityIds.map(id => normalizedCities.find(city => city.id === id)?.name || `Город ${id}`).join(', ') : 'не найдены'}.
          </p>
        )}
        {normalizedCities.length > 1 && !selectedOption && (
          <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
            <div>
              <p className="text-sm font-medium">Отметьте города работы педагога</p>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              {normalizedCities.map(city => (
                <label key={city.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={cityIds.includes(city.id)}
                    onChange={event => setCityIds(current => event.target.checked ? [...current, city.id] : current.filter(id => id !== city.id))}
                  />
                  {city.name}
                </label>
              ))}
            </div>
          </div>
        )}
        <Button size="sm" onClick={() => void sendInvite()} disabled={busy === 'invite' || (!manual && !selectedTeacherId) || (manual && (!firstName.trim() || !lastName.trim())) || (manual && !emailMatchChecked) || (manual && Boolean(emailMatch) && !emailConfirmed) || !email.trim() || cityIds.length === 0}>
          {busy === 'invite' ? 'Отправляем…' : 'Отправить приглашение'}
        </Button>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {states.length > 0 && <div className="rounded-xl border border-border">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium"
          onClick={() => setShowStates(value => !value)}
        >
          <span>Педагоги франчайзи ({states.length})</span>
          <span className="text-xs text-muted-foreground">{showStates ? 'Скрыть' : 'Показать'}</span>
        </button>
        {showStates && <div className="space-y-2 border-t border-border p-3">
        {states.map(row => {
          const pendingInvite = Boolean(row.invite_id)
          const roleStatus = row.request_status
          const needsTeamInvite = Boolean(row.user_id && !row.has_active_team_membership)
          return (
            <div key={row.teacher_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 font-medium">
                  <span>{row.full_name}</span>
                  {roleStatus === 'approved' && <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">Старший педагог</span>}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {needsTeamInvite
                    ? (pendingInvite ? 'Ожидает принятия приглашения' : 'Аккаунт зарегистрирован, но педагог не добавлен в команду')
                    : row.user_id ? 'Педагог состоит в команде' : pendingInvite ? 'Инвайт отправлен' : 'Не приглашён'}
                  {roleStatus === 'pending' && ' · Запрос отправлен, ожидайте одобрения УК'}
                  {roleStatus === 'rejected' && ' · Заявка отклонена, обратитесь в УК'}
                  {roleStatus === 'cancelled' && ' · Запрос отменён'}
                  {roleStatus === 'revoked' && row.request_revoked_by_scope === 'management' && ' · Доступ старшего педагога снят управляющей компанией'}
                  {roleStatus === 'revoked' && row.request_revoked_by_scope !== 'management' && ' · Доступ старшего педагога снят'}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {row.user_id && !row.has_active_team_membership && !pendingInvite && (
                  <Button size="sm" disabled={busy === `reinvite-${row.teacher_id}`} onClick={() => void run(`reinvite-${row.teacher_id}`, async () => {
                    const result = await createTeacherReinvite(row.teacher_id, partnerNumber, normalizedCities.map(city => city.id))
                    await callAuth('email-request', { email: result.targetEmail, invite_token: result.token })
                  })}>Отправить инвайт повторно</Button>
                )}
                {pendingInvite && row.invite_token && (
                  <>
                    <Button variant="outline" size="sm" disabled={busy === `resend-invite-${row.teacher_id}`} onClick={() => void run(`resend-invite-${row.teacher_id}`, async () => {
                      await callAuth('email-request', { email: row.invite_email, invite_token: row.invite_token })
                    })}>Повторить отправку</Button>
                    <Button variant="outline" size="sm" disabled={busy === `revoke-invite-${row.teacher_id}`} onClick={() => void run(`revoke-invite-${row.teacher_id}`, () => revokeTeacherInvite(row.invite_id!))}>Отозвать инвайт</Button>
                  </>
                )}
                {row.user_id && row.has_active_team_membership && (!roleStatus || roleStatus === 'rejected' || roleStatus === 'cancelled') && (
                  <Button
                    size="sm"
                    title="После отправки запрос рассмотрит управляющая компания"
                    disabled={busy === `request-${row.teacher_id}`}
                    onClick={() => void run(`request-${row.teacher_id}`, () => createTeacherRoleRequest(row.teacher_id, partnerNumber))}
                  >
                    Запросить повышение до старшего педагога
                  </Button>
                )}
                {row.user_id && !row.has_active_team_membership && !pendingInvite && !roleStatus && (
                  <span className="text-xs text-muted-foreground">После принятия инвайта можно будет запросить повышение до старшего педагога</span>
                )}
                {roleStatus === 'pending' && row.request_id && (
                  <Button variant="outline" size="sm" disabled={busy === `cancel-${row.teacher_id}`} onClick={() => void run(`cancel-${row.teacher_id}`, () => cancelTeacherRoleRequest(row.request_id!))}>Отменить запрос</Button>
                )}
                {roleStatus === 'approved' && row.request_id && (
                  <Button variant="outline" size="sm" disabled={busy === `revoke-role-${row.teacher_id}`} onClick={() => void run(`revoke-role-${row.teacher_id}`, () => revokeTeacherRoleRequest(row.request_id!))}>Снять доступ старшего педагога</Button>
                )}
                {roleStatus === 'revoked' && row.request_id && row.request_revoked_by_scope === 'franchisee' && row.has_active_team_membership && (
                  <Button size="sm" disabled={busy === `restore-role-${row.teacher_id}`} onClick={() => void run(`restore-role-${row.teacher_id}`, () => restoreTeacherRoleAccess(row.request_id!))}>Вернуть доступ старшего педагога</Button>
                )}
                {roleStatus === 'revoked' && row.request_id && row.request_revoked_by_scope !== 'franchisee' && row.has_active_team_membership && (
                  <Button size="sm" disabled={busy === `request-${row.teacher_id}`} onClick={() => void run(`request-${row.teacher_id}`, () => createTeacherRoleRequest(row.teacher_id, partnerNumber))}>Отправить на повторное рассмотрение</Button>
                )}
                {roleStatus === 'revoked' && row.request_id && !row.has_active_team_membership && (
                  <span className="text-xs text-muted-foreground">После принятия инвайта можно будет отправить новую заявку на рассмотрение</span>
                )}
              </div>
            </div>
          )
        })}
        </div>}
      </div>}
    </div>
  )
}
